import _find from 'lodash-es/find';
import _uniq from 'lodash-es/uniq';

import {
  httpDeleteByObjectTypeCategoriesByName,
  httpGetByObjectTypeCategories,
  httpGetByObjectTypeCategoriesByNameViews,
  httpPostByObjectTypeCategories,
  httpPutByObjectTypeCategoriesByName,
  type MetadataMetadataCategorySchema,
  type MetadataMetadataCategorySchemaNullable,
} from '@iconik-io/iconik-api/src/services/metadata';

import type { AppDispatch, GetState } from '../../../store/types';
import { allCategoryObjectTypes } from './constants';
import { setMetadataCategoriesOfType, setMetadataCategory } from './slice';
import type {
  CategoryName,
  CategoryObjectType,
  MetadataMetadataCategorySchemaPatched,
} from './types';

export const patchBackendCategory = (
  item: MetadataMetadataCategorySchemaNullable,
): MetadataMetadataCategorySchemaPatched =>
  // biome-ignore lint/plugin: a category is keyed by its name, which the patched shape states as the id.
  ({
    ...item,
    id: item.name,
  }) as MetadataMetadataCategorySchemaPatched;

export function getMetadataCategoryViews(
  objectType: CategoryObjectType,
  name: CategoryName,
) {
  return (dispatch: AppDispatch) =>
    dispatch(httpGetByObjectTypeCategoriesByNameViews(objectType, name)).then(
      (response) => {
        dispatch(
          setMetadataCategory({
            category: patchBackendCategory(response.body),
            objectType,
            name,
          }),
        );
        return response.body;
      },
    );
}

export function getAllMetadataCategoryTypes() {
  return (dispatch: AppDispatch) =>
    Promise.all(
      allCategoryObjectTypes.map((objectType) =>
        dispatch(getMetadataCategoriesOfType(objectType)),
      ),
    );
}

export function getMetadataCategoriesOfType(objectType: CategoryObjectType) {
  return (dispatch: AppDispatch) =>
    dispatch(httpGetByObjectTypeCategories(objectType)).then((response) => {
      const categories = response.body.objects.map(patchBackendCategory);
      dispatch(
        setMetadataCategoriesOfType({
          categories,
          objectType,
        }),
      );
      return categories;
    });
}

export function createMetadataCategory(
  objectType: CategoryObjectType,
  data: MetadataMetadataCategorySchema,
) {
  return (dispatch: AppDispatch, getState: GetState) =>
    dispatch(httpPostByObjectTypeCategories(objectType, data)).then(
      (response) => {
        const categories =
          getState().metadataCategories.metadataTypeCategories[objectType] ??
          [];
        const newCategory = patchBackendCategory(response.body);
        dispatch(
          setMetadataCategoriesOfType({
            categories: [newCategory].concat(categories),
            objectType,
          }),
        );
        return newCategory;
      },
    );
}

export function updateMetadataCategoryViews(
  objectType: CategoryObjectType,
  name: string,
  label: string,
  viewIds: string[],
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    // replace view_ids with the same order
    const categories =
      getState().metadataCategories.metadataTypeCategories[objectType];
    if (!categories) {
      return Promise.resolve();
    }
    const category: MetadataMetadataCategorySchema = {
      name: '',
      ..._find(categories, { name }),
      label,
      view_ids: _uniq(viewIds),
    };
    const updatedCategories = categories.map((c) =>
      c.name === name ? category : c,
    );

    return dispatch(
      httpPutByObjectTypeCategoriesByName(objectType, name, category),
    ).then(() => {
      dispatch(getMetadataCategoryViews(objectType, name));
      dispatch(
        setMetadataCategoriesOfType({
          categories: updatedCategories.map(patchBackendCategory),
          objectType,
        }),
      );
    });
  };
}

export function deleteMetadataCategoriesByIds(
  objectType: CategoryObjectType,
  ids: string[],
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> =>
    Promise.all(
      ids.map((id) =>
        dispatch(httpDeleteByObjectTypeCategoriesByName(objectType, id)),
      ),
    ).then(() => {
      const categories =
        getState().metadataCategories.metadataTypeCategories[objectType] ?? [];
      dispatch(
        setMetadataCategoriesOfType({
          categories: categories.filter(
            (category) => !ids.includes(category.id),
          ),
          objectType,
        }),
      );
    });
}
